import React, {useEffect, useState} from 'react';
import { useNuStreamerContext } from '../NuStreamerContext';
import classnames from "classnames";
import LoadingIcon from "../../../components/LoadingIcon";

const componentClass = 'nuStreamerVideo';

const NuStreamerVideo = ({ className, children, width, height, playsInline, useControls, usePip, elementRef, image, onVideoClick }) => {
    const [showSpinner, setShowSpinner] = useState(false);
    const [hasPlayed, setHasPlayed] = useState(false);
    const {
        stopVideoPlayer,
        streamerMode,
        STREAMER_MODES,
        setVideoListIndex,
        videoListIndex,
        videoList,
        setIsPlayingVideo,
        setActiveVideo,
        setTimeProgress,
        timeProgress,
        setDuration,
        setIsSeeking,
        isSeeking,
        activeVideo
    } = useNuStreamerContext();
    const classes = classnames(componentClass, className);
    const isRadioMode = streamerMode === STREAMER_MODES.BROADCAST;
    const isPlaylistMode = streamerMode === STREAMER_MODES.PLAYLIST;

    const handleTheEnd = () => {
        const thisVideo = elementRef.current;
        if (isRadioMode || isPlaylistMode) {
            let nextVideoIndex = null;
            // need to go to next track
            if (videoListIndex === (videoList || []).length - 1 ) {
                nextVideoIndex = 0;
            } else {
                nextVideoIndex = videoListIndex + 1;
            }
            const matchedVideoRecord = videoList[nextVideoIndex];
            if (matchedVideoRecord && matchedVideoRecord.hasOwnProperty('videoId') && matchedVideoRecord.hasOwnProperty('videoSource')) {
                setActiveVideo({
                    videoId: activeVideo.videoId,
                    videoSource: matchedVideoRecord.videoSource,
                    config: matchedVideoRecord
                });
                setVideoListIndex(nextVideoIndex);
                thisVideo.load();
                setTimeProgress(0);
                //don't want to autostart video if playlist ended...
                if(isRadioMode || nextVideoIndex > 0) {
                    thisVideo.play();
                }
            }
        } else {
            if(!thisVideo?.keepOpenOnEnd) {
                stopVideoPlayer();
            }
            thisVideo.load();
        }
    };

    //act like we have not played anything when the child sources change
    useEffect(() => {
        setHasPlayed(false);
    }, [children]);

    useEffect(() => {
        const thisVideo = elementRef.current;
        thisVideo.playerProgressTick = 0;
        if (thisVideo) {
            //remove download button from video and pip
            thisVideo.setAttribute('controlslist', 'nodownload');
            //stop the player when video ends (or go to next video in broadcast / playlist mode)
            thisVideo.onended = () => handleTheEnd();
            //pause event listener
            thisVideo.onpause = () => {
                setIsPlayingVideo(false);
                if(isRadioMode) {
                    if (thisVideo.hasOwnProperty('isStoppingBroadcast') && thisVideo.isStoppingBroadcast) {
                        console.debug('Stopping broadcast video...');
                    } else {
                        thisVideo.play();
                    }
                    thisVideo.isStoppingBroadcast = false;
                }
            };
            //play event listener
            thisVideo.onplay = () => {
                setHasPlayed(true);
                setIsPlayingVideo(true);
            };
            //capture progress of current video
            //only want to capture valid progress, radio mode time changes should not be larger than 1 second
            thisVideo.ontimeupdate = () => {
                if (!isSeeking) {
                    const timeDifference = Math.abs(thisVideo.currentTime - timeProgress);
                    if (!isRadioMode || timeProgress < 1 || timeDifference < 1) {
                        setTimeProgress(thisVideo.currentTime);
                    } else {
                        console.debug('Disregarding time update', {isRadioMode, timeDifference});
                    }
                }
            };
            //get video duration
            const seconds = thisVideo.duration;
            if (seconds !== undefined) {
                setDuration(seconds);
                thisVideo.max = seconds.toString();
            }
            thisVideo.onseeking = () => {
                setIsSeeking(true);
            };
            //disable seeking results in radio mode
            thisVideo.onseeked = () => {
                setIsSeeking(false);
                if(isRadioMode) {
                    if (!thisVideo.someoneIsSeeking) {
                        //someone has moved time but did not signal for a seek, so we will signal for a seek and move the time back to where it was at
                        thisVideo.someoneIsSeeking = true;
                        thisVideo.currentTime = timeProgress;
                    } else {
                        //someone told us they were seeking...let the time change pass through and reset the signal
                        thisVideo.someoneIsSeeking = false;
                    }
                }
            };
            //listen for loading signal
            thisVideo.onloadstart = () => {
                setShowSpinner(true);
            };
            thisVideo.oncanplay = () => {
                setShowSpinner(false);
            };
            thisVideo.onloadeddata = () => {
                setShowSpinner(false);
            };
        }

        return () => {
            if (thisVideo) {
                thisVideo.onended = null;
                thisVideo.onpause = null;
                thisVideo.onplay = null;
                thisVideo.ontimeupdate = null;
                thisVideo.onseeking = null;
                thisVideo.onseeked = null;
                thisVideo.onloadstart = null;
                thisVideo.oncanplay = null;
                thisVideo.onloadeddata = null;
            }
        };

    }, [elementRef, handleTheEnd, isRadioMode, isPlaylistMode, isSeeking]);

    return <div className={`${componentClass}__container`} style={{height, width}}>
        <video
            className={classes}
            controls={useControls}
            width={width}
            height={height}
            playsInline={playsInline}
            disablePictureInPicture={!usePip}
            ref={elementRef}
            poster={streamerMode === STREAMER_MODES.BROADCAST ? null : image}
            onClick={onVideoClick}
        >
            { children }
        </video>
        { hasPlayed && showSpinner && <LoadingIcon className={`${componentClass}__loadingIcon`} /> }
    </div>;
}

export default NuStreamerVideo;
